(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 767;
  const WHEEL_SENSITIVITY = 18;

  const HORIZONTAL_SWIPER_DEFAULTS = {
    slidesPerGroup: 1,
    freeMode: false,
    width: null,
    autoHeight: false,
    resistance: true,
    resistanceRatio: 0.85,
    speed: 300,
    loop: false,
  };

  /** Shared breakpoint presets — each section picks one and adds its own UI hooks */
  window.HORIZONTAL_SWIPER_PRESETS = {
    gallery: {
      slidesPerView: 1.2,
      spaceBetween: 16,
      breakpoints: {
        567: { slidesPerView: 2.2, spaceBetween: 16 },
        768: { slidesPerView: 2.95, spaceBetween: 16 },
        1025: { slidesPerView: 3.925, spaceBetween: 16 },
      },
    },
    youMayAlsoLike: {
      slidesPerView: 1.5,
      spaceBetween: 0,
      breakpoints: {
        640: { slidesPerView: 2.2, spaceBetween: 0 },
        1024: { slidesPerView: 3.2, spaceBetween: 0 },
        1280: { slidesPerView: 4, spaceBetween: 0 },
      },
    },
    technologyPdp: {
      slidesPerView: 1,
      spaceBetween: 16,
      breakpoints: {
        768: { slidesPerView: 2.2, spaceBetween: 16 },
        1024: { slidesPerView: 3.15, spaceBetween: 16 },
      },
    },
    cruiserTechnology: {
      slidesPerView: 1.1,
      spaceBetween: 5,
      loop: true,
      breakpoints: {
        640: { slidesPerView: 1.4 },
        768: { slidesPerView: 2 },
        990: { slidesPerView: 2.5 },
        1280: { slidesPerView: 3 },
      },
    },
    hatFits: {
      mobileSlidesPerView: 1,
      desktopSlidesPerView: 3.5,
      spaceBetween: 8,
    },
    collectionTab: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
  };

  function attachHorizontalWheelScroll(swiperEl, getSwiper, sensitivity, onAfterTranslate) {
    const wheelSensitivity = typeof sensitivity === 'number' ? sensitivity : WHEEL_SENSITIVITY;
    if (!wheelSensitivity) return;

    let accumulatedWheelDelta = 0;
    let wheelRafId = null;

    swiperEl.addEventListener(
      'wheel',
      function (e) {
        const swiper = getSwiper();
        if (!swiper) return;
        const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
        if (!isHorizontal) return;

        accumulatedWheelDelta += -e.deltaX * wheelSensitivity;
        if (Math.abs(accumulatedWheelDelta) < 0.5) return;

        e.preventDefault();
        if (wheelRafId != null) return;

        wheelRafId = requestAnimationFrame(function applyWheelFrame() {
          wheelRafId = null;
          const activeSwiper = getSwiper();
          if (!activeSwiper || Math.abs(accumulatedWheelDelta) < 0.5) return;

          const swiperMin = activeSwiper.minTranslate();
          const swiperMax = activeSwiper.maxTranslate();
          const minT = Math.min(swiperMin, swiperMax);
          const maxT = Math.max(swiperMin, swiperMax);
          const t =
            typeof activeSwiper.getTranslate === 'function'
              ? activeSwiper.getTranslate()
              : activeSwiper.translate;
          const delta = accumulatedWheelDelta;
          accumulatedWheelDelta = 0;
          const newT = Math.max(minT, Math.min(maxT, t + delta));

          if (typeof activeSwiper.setTransition === 'function') {
            activeSwiper.setTransition(0);
          }
          activeSwiper.setTranslate(newT);
          if (typeof activeSwiper.setTransition === 'function') {
            activeSwiper.setTransition(300);
          }

          if (typeof onAfterTranslate === 'function') {
            const progress = maxT !== minT ? (newT - maxT) / (minT - maxT) : 0;
            onAfterTranslate(activeSwiper, progress);
          }
        });
      },
      { passive: false },
    );
  }

  /**
   * Tear down a Swiper instance left on a replaced or stale element so a fresh init can bind touch.
   * @param {HTMLElement} el
   */
  function destroySwiperElement(el) {
    if (!(el instanceof HTMLElement)) return;

    const instance = el.swiper || el._swiper;
    if (instance && typeof instance.destroy === 'function') {
      try {
        instance.destroy(true, true);
      } catch (_error) {
        // Stale instances from SPA section swaps can throw during destroy.
      }
    }

    el.swiper = undefined;
    el._swiper = null;
    el.classList.remove(
      'swiper-initialized',
      'swiper-horizontal',
      'swiper-backface-hidden',
      'swiper-watch-progress',
    );
  }

  function createHorizontalSwiperInstance(swiperEl, config) {
    let instance = null;
    const {
      spaceBetween = 0,
      slidesPerView = 1,
      breakpoints,
      centeredSlides = false,
      centeredSlidesBounds = false,
      grabCursor = true,
      allowTouchMove = true,
      navigation,
      scrollbar,
      pagination = false,
      wheelSensitivity = WHEEL_SENSITIVITY,
      wheelOnAfterTranslate,
      on = {},
    } = config;

    const mergedOn = { ...on };
    const userInit = mergedOn.init;
    mergedOn.init = function () {
      if (wheelSensitivity && swiperEl) {
        attachHorizontalWheelScroll(
          swiperEl,
          () => instance,
          wheelSensitivity,
          wheelOnAfterTranslate,
        );
      }
      if (typeof userInit === 'function') userInit.call(this);
    };

    const swiperOptions = {
      ...HORIZONTAL_SWIPER_DEFAULTS,
      spaceBetween,
      slidesPerView,
      centeredSlides,
      centeredSlidesBounds,
      grabCursor,
      allowTouchMove,
      on: mergedOn,
    };

    if (breakpoints && Object.keys(breakpoints).length) {
      swiperOptions.breakpoints = breakpoints;
    }
    if (navigation) swiperOptions.navigation = navigation;
    if (scrollbar) swiperOptions.scrollbar = scrollbar;
    if (pagination) swiperOptions.pagination = pagination;

    instance = new Swiper(swiperEl, swiperOptions);
    return instance;
  }

  if (!window.initProductShowcaseCardImageSwipers) {
    window.initProductShowcaseCardImageSwipers = function (root) {
      if (typeof window.initProductImageSwipers === 'function') {
        return window.initProductImageSwipers(root);
      }

      setTimeout(() => window.initProductShowcaseCardImageSwipers(root), 100);
      return [];
    };
  }

  if (!window.initGallerySwipers) {
    function updateGalleryNavVisibility(instance) {
      if (!instance?.navigation) return;
      const nextEl = instance.navigation.nextEl;
      const prevEl = instance.navigation.prevEl;
      const display = window.innerWidth >= 768 ? 'none' : 'none';
      if (nextEl) nextEl.style.display = display;
      if (prevEl) prevEl.style.display = display;
    }

    window.initGallerySwipers = function (root, options) {
      const container = root || document;
      const force = Boolean(options && options.force);
      const instances = [];

      if (typeof Swiper === 'undefined') {
        setTimeout(() => window.initGallerySwipers(container, options), 100);
        return instances;
      }

      container.querySelectorAll('[data-gallery-swiper]').forEach((gallerySwiper) => {
        if (force) {
          destroySwiperElement(gallerySwiper);
        } else if (gallerySwiper.classList.contains('swiper-initialized')) {
          return;
        }

        let swiperInstance = null;
        swiperInstance = createHorizontalSwiperInstance(gallerySwiper, {
          slidesPerView: 1.2,
          spaceBetween: 16,
          grabCursor: false,
          navigation: {
            nextEl: gallerySwiper.querySelector('.swiper-button-next'),
            prevEl: gallerySwiper.querySelector('.swiper-button-prev'),
          },
          scrollbar: {
            el: gallerySwiper.querySelector('.swiper-scrollbar'),
            draggable: true,
          },
          breakpoints: window.HORIZONTAL_SWIPER_PRESETS.gallery.breakpoints,
          wheelSensitivity: WHEEL_SENSITIVITY,
          on: {
            init: function () {
              updateGalleryNavVisibility(this);
            },
            breakpoint: function () {
              updateGalleryNavVisibility(this);
            },
          },
        });

        window.addEventListener('resize', function () {
          updateGalleryNavVisibility(swiperInstance);
        });

        instances.push(swiperInstance);
      });

      // Section swaps can mount before layout settles; refresh sizes so touch math is correct.
      requestAnimationFrame(() => {
        for (const instance of instances) {
          if (instance && typeof instance.update === 'function') instance.update();
        }
      });

      return instances;
    };
  }

  if (!window.initTechnologySwipers) {
    let technologyResizeRegistered = false;
    let technologyDesktopCount = 3.14;

    function getTechnologySlideCount(sliderElement) {
      const sliderWrapper = sliderElement.querySelector('.swiper-wrapper');
      if (sliderWrapper) {
        const uniqueSlides = Array.from(sliderWrapper.children).filter(
          (slide) => !slide.classList.contains('swiper-slide-duplicate'),
        );
        if (uniqueSlides.length > 0) return uniqueSlides.length;
      }
      return parseInt(sliderElement.getAttribute('data-slide-count'), 10) || 0;
    }

    function getTechnologyNavButtons(sliderElement, isMobile) {
      const section = sliderElement.closest('.technology-section');
      return {
        nextEl: isMobile
          ? sliderElement.querySelector('.technology-next-button-mobile')
          : section?.querySelector('.technology-navigation .technology-next-button'),
        prevEl: isMobile
          ? sliderElement.querySelector('.technology-prev-button-mobile')
          : section?.querySelector('.technology-navigation .technology-prev-button'),
      };
    }

    function positionTechnologyMobileElements(sliderElement) {
      const pagination = sliderElement.querySelector('.swiper-pagination');
      const prevBtn = sliderElement.querySelector('.technology-prev-button-mobile');
      const nextBtn = sliderElement.querySelector('.technology-next-button-mobile');
      const image = sliderElement.querySelector('.swiper-slide-active .technology-image');

      if (!image) return;

      requestAnimationFrame(function () {
        const imageHeight = image.offsetHeight;
        const imageTop = image.getBoundingClientRect().top - sliderElement.getBoundingClientRect().top;
        const buttonTop = imageTop + imageHeight / 2;

        if (prevBtn) {
          prevBtn.style.top = buttonTop + 'px';
          prevBtn.style.pointerEvents = 'auto';
          prevBtn.style.zIndex = '10';
        }
        if (nextBtn) {
          nextBtn.style.top = buttonTop + 'px';
          nextBtn.style.pointerEvents = 'auto';
          nextBtn.style.zIndex = '10';
        }
        if (pagination) {
          pagination.style.top = imageTop + imageHeight + 16 + 'px';
        }
      });
    }

    function positionTechnologyMobileElementsFallback(sliderEl) {
      if (sliderEl._positionElements && typeof sliderEl._positionElements === 'function') {
        sliderEl._positionElements();
        return;
      }
      positionTechnologyMobileElements(sliderEl);
    }

    function updateTechnologyDesktopButtonStates(sliderEl) {
      const desktopPrevBtn = sliderEl
        .closest('.technology-section')
        ?.querySelector('.technology-navigation .technology-prev-button');
      const desktopNextBtn = sliderEl
        .closest('.technology-section')
        ?.querySelector('.technology-navigation .technology-next-button');

      if (desktopPrevBtn) {
        desktopPrevBtn.style.pointerEvents = 'auto';
        desktopPrevBtn.style.zIndex = '10';
      }
      if (desktopNextBtn) {
        desktopNextBtn.style.pointerEvents = 'auto';
        desktopNextBtn.style.zIndex = '10';
      }

      const mobilePrevBtn = sliderEl.querySelector('.technology-prev-button-mobile');
      const mobileNextBtn = sliderEl.querySelector('.technology-next-button-mobile');
      if (mobilePrevBtn) {
        mobilePrevBtn.style.pointerEvents = 'none';
        mobilePrevBtn.style.zIndex = '1';
      }
      if (mobileNextBtn) {
        mobileNextBtn.style.pointerEvents = 'none';
        mobileNextBtn.style.zIndex = '1';
      }
    }

    function handleTechnologyResize() {
      const currentIsMobile = window.innerWidth <= MOBILE_BREAKPOINT;

      document.querySelectorAll('.technology-slider.swiper-initialized').forEach(function (sliderEl) {
        const swiper = sliderEl._swiper;
        if (!swiper) return;

        const wasMobile = sliderEl._isMobile;
        sliderEl._isMobile = currentIsMobile;

        if (wasMobile !== currentIsMobile) {
          swiper.params.slidesPerView = currentIsMobile ? 2.2 : technologyDesktopCount;
          swiper.params.spaceBetween = 16;
          swiper.update();

          const { nextEl: newNextButton, prevEl: newPrevButton } = getTechnologyNavButtons(
            sliderEl,
            currentIsMobile,
          );

          if (newNextButton && newPrevButton) {
            if (swiper.navigation?.destroy) {
              swiper.navigation.destroy();
            }
            swiper.params.navigation.nextEl = newNextButton;
            swiper.params.navigation.prevEl = newPrevButton;
            swiper.navigation.init();
            swiper.navigation.update();
            newNextButton.style.pointerEvents = 'auto';
            newPrevButton.style.pointerEvents = 'auto';
            newNextButton.style.zIndex = '10';
            newPrevButton.style.zIndex = '10';
            setTimeout(function () {
              swiper.navigation?.update?.();
            }, 50);
          }
        }

        if (currentIsMobile) {
          positionTechnologyMobileElementsFallback(sliderEl);
        } else {
          updateTechnologyDesktopButtonStates(sliderEl);
        }
      });
    }

    function registerTechnologyResizeHandler(isMobile) {
      if (technologyResizeRegistered) return;
      technologyResizeRegistered = true;

      let resizeTimeout;
      const debouncedResizeHandler = function () {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(handleTechnologyResize, 100);
      };

      window.addEventListener('resize', debouncedResizeHandler);

      if (isMobile) {
        setTimeout(handleTechnologyResize, 100);
      }
    }

    window.initTechnologySwipers = function (root, options) {
      const container = root || document;
      const instances = [];
      const opts = options || {};
      const force = Boolean(opts.force);

      if (typeof opts.desktopCount === 'number') {
        technologyDesktopCount = opts.desktopCount;
      }

      if (typeof Swiper === 'undefined') {
        setTimeout(() => window.initTechnologySwipers(container, options), 100);
        return instances;
      }

      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const sliders = force
        ? container.querySelectorAll('.technology-slider')
        : container.querySelectorAll('.technology-slider:not(.swiper-initialized)');

      if (!sliders.length) return instances;

      sliders.forEach(function (sliderElement) {
        if (force) {
          destroySwiperElement(sliderElement);
        }

        const slideCount = getTechnologySlideCount(sliderElement);
        const desktopSlidesPerView = slideCount === 3 ? 3 : 3.15;
        const { nextEl: nextButton, prevEl: prevButton } = getTechnologyNavButtons(sliderElement, isMobile);

        let swiperInstance = null;

        const swiperConfig = {
          spaceBetween: 16,
          slidesPerView: 1,
          grabCursor: false,
          allowTouchMove: true,
          navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
          },
          scrollbar: {
            el: sliderElement.querySelector('.swiper-scrollbar'),
            draggable: true,
          },
          breakpoints: {
            1024: {
              slidesPerView: desktopSlidesPerView,
              spaceBetween: 16,
            },
            768: {
              slidesPerView: 2.2,
              spaceBetween: 16,
            },
          },
          wheelSensitivity: WHEEL_SENSITIVITY,
        };

        if (isMobile) {
          const paginationEl = sliderElement.querySelector('.swiper-pagination');
          if (paginationEl) {
            swiperConfig.pagination = {
              el: paginationEl,
              clickable: true,
              renderBullet: function (index, className) {
                return '<span class="' + className + '"></span>';
              },
            };
          }
        }

        sliderElement._slideCount = slideCount;
        swiperInstance = createHorizontalSwiperInstance(sliderElement, swiperConfig);
        sliderElement._swiper = swiperInstance;
        sliderElement._isMobile = isMobile;

        if (!isMobile && nextButton && prevButton) {
          nextButton.style.pointerEvents = 'auto';
          prevButton.style.pointerEvents = 'auto';
          nextButton.style.zIndex = '10';
          prevButton.style.zIndex = '10';
        }

        if (isMobile) {
          sliderElement._positionElements = function () {
            positionTechnologyMobileElements(sliderElement);
          };
          positionTechnologyMobileElements(sliderElement);
          swiperInstance.on('slideChange', function () {
            setTimeout(function () {
              positionTechnologyMobileElements(sliderElement);
            }, 50);
          });
        }

        instances.push(swiperInstance);
      });

      registerTechnologyResizeHandler(isMobile);

      requestAnimationFrame(() => {
        for (const instance of instances) {
          if (instance && typeof instance.update === 'function') instance.update();
        }
      });

      return instances;
    };
  }

  if (!window.initHeroProductShowcaseMainSwiper) {
    const HERO_MOBILE_BREAKPOINT = 767;

    window.initHeroProductShowcaseMainSwiper = function (options) {
      const {
        section,
        swiperElement,
        progressElement,
        progressBarFill,
        paginationElement,
        existingInstance,
        skeletonMode = false,
        onProductImagesInit,
      } = options || {};

      if (!swiperElement || typeof Swiper === 'undefined') {
        return null;
      }

      if (existingInstance) {
        existingInstance.destroy(true, true);
      }

      let resizeTimeout = null;
      let swiperInstance = null;

      const updateProgressBar = (swiper, progressOverride, dragState) => {
        if (!progressBarFill || !swiper) return;
        if (progressElement && progressElement.classList.contains('is-dragging') && dragState === undefined) {
          return;
        }
        const thumbWidthPercent = 20;
        const maxLeft = 100 - thumbWidthPercent;
        let progress =
          progressOverride !== undefined
            ? Math.min(1, Math.max(0, progressOverride))
            : (() => {
                const minT = typeof swiper.minTranslate === 'function' ? swiper.minTranslate() : 0;
                const maxT = typeof swiper.maxTranslate === 'function' ? swiper.maxTranslate() : 0;
                const t = typeof swiper.getTranslate === 'function' ? swiper.getTranslate() : swiper.translate;
                const lo = Math.min(minT, maxT);
                const hi = Math.max(minT, maxT);
                if (hi === lo) return 0;
                return Math.min(1, Math.max(0, (t - hi) / (lo - hi)));
              })();
        if (Number.isNaN(progress) || !Number.isFinite(progress)) progress = 0;
        progressBarFill.classList.toggle('hero-product-showcase__progress-bar--empty', progress <= 0);
        const leftPct = progress * maxLeft;
        if (dragState && dragState.rect) {
          const translatePx = progress * (dragState.rect.width * (1 - thumbWidthPercent / 100));
          progressBarFill.style.transform = 'translateX(' + translatePx + 'px)';
          progressBarFill.style.left = '0';
        } else {
          progressBarFill.style.transform = '';
          progressBarFill.style.left = leftPct + '%';
        }
      };

      const applyProgressToSwiper = (swiper, progress, dragState) => {
        if (!swiper || swiper.slides.length <= 1) return;
        const swiperMin = typeof swiper.minTranslate === 'function' ? swiper.minTranslate() : 0;
        const swiperMax = typeof swiper.maxTranslate === 'function' ? swiper.maxTranslate() : 0;
        const minT = Math.min(swiperMin, swiperMax);
        const maxT = Math.max(swiperMin, swiperMax);
        const newT = maxT + progress * (minT - maxT);
        if (typeof swiper.setTransition === 'function') swiper.setTransition(0);
        swiper.setTranslate(newT);
        updateProgressBar(swiper, progress, dragState);
      };

      const updatePaginationDots = (swiper, activeIndexOverride) => {
        if (!paginationElement || !swiper) return;
        const count = swiper.slides.length;
        if (count <= 0) return;
        const activeIndex = activeIndexOverride !== undefined ? activeIndexOverride : (swiper.activeIndex ?? 0);
        if (paginationElement.children.length !== count) {
          paginationElement.innerHTML = '';
          for (let i = 0; i < count; i++) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'hero-product-showcase__pagination-dot';
            button.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            button.addEventListener('click', () => {
              if (swiperInstance) swiperInstance.slideTo(i, 300);
            });
            paginationElement.appendChild(button);
          }
        }
        paginationElement.querySelectorAll('.hero-product-showcase__pagination-dot').forEach((dot, i) => {
          dot.classList.toggle('hero-product-showcase__pagination-dot--active', i === activeIndex);
        });
      };

      const setupProgressBarDrag = (progressBarEl, swiper) => {
        if (!progressBarEl || !swiper) return;
        if (progressBarEl._heroProgressDragSetup) {
          progressBarEl._heroProgressSwiper = swiper;
          return;
        }
        progressBarEl._heroProgressDragSetup = true;
        progressBarEl._heroProgressSwiper = swiper;

        function getProgress(clientX, rect) {
          const r = rect || progressBarEl.getBoundingClientRect();
          return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
        }

        let activePointerId = null;
        let dragRafId = null;
        let lastDragClientX = null;
        let dragRect = null;

        function applyDragFrame() {
          dragRafId = null;
          const x = lastDragClientX;
          if (x == null || !dragRect) return;
          const s = progressBarEl._heroProgressSwiper;
          if (!s) return;
          applyProgressToSwiper(s, getProgress(x, dragRect), { rect: dragRect });
        }

        function onPointerMove(e) {
          if (e.pointerType === 'touch') e.preventDefault();
          lastDragClientX = e.clientX;
          if (dragRafId != null) return;
          dragRafId = requestAnimationFrame(applyDragFrame);
        }

        function endDrag(e) {
          if (activePointerId !== null && e.pointerId !== activePointerId) return;
          activePointerId = null;
          lastDragClientX = null;
          dragRect = null;
          if (dragRafId != null) {
            cancelAnimationFrame(dragRafId);
            dragRafId = null;
          }
          progressBarEl.classList.remove('is-dragging');
          try {
            progressBarEl.releasePointerCapture(e.pointerId);
          } catch (err) {}
          progressBarEl.removeEventListener('pointermove', onPointerMove);
          progressBarEl.removeEventListener('pointerup', endDrag);
          progressBarEl.removeEventListener('pointercancel', endDrag);
          progressBarEl.removeEventListener('pointerleave', onPointerLeave);
          const s = progressBarEl._heroProgressSwiper;
          if (s && s.slides.length > 1) {
            if (typeof s.setTransition === 'function') s.setTransition(300);
            let progress = 0;
            if (
              typeof s.getTranslate === 'function' &&
              typeof s.minTranslate === 'function' &&
              typeof s.maxTranslate === 'function'
            ) {
              const minT = Math.min(s.minTranslate(), s.maxTranslate());
              const maxT = Math.max(s.minTranslate(), s.maxTranslate());
              const t = s.getTranslate();
              progress = maxT !== minT ? Math.min(1, Math.max(0, (t - maxT) / (minT - maxT))) : 0;
            }
            if (Number.isNaN(progress) || !Number.isFinite(progress)) progress = 0;
            progressBarEl.classList.add('is-syncing');
            updateProgressBar(s, progress);
            requestAnimationFrame(function () {
              progressBarEl.classList.remove('is-syncing');
            });
          }
        }

        function onPointerLeave(e) {
          if (activePointerId !== null && e.pointerId === activePointerId) endDrag(e);
        }

        progressBarEl.addEventListener(
          'pointerdown',
          function (e) {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            if (e.cancelable) e.preventDefault();
            activePointerId = e.pointerId;
            dragRect = progressBarEl.getBoundingClientRect();
            progressBarEl.classList.add('is-dragging');
            applyProgressToSwiper(progressBarEl._heroProgressSwiper, getProgress(e.clientX, dragRect), {
              rect: dragRect,
            });
            progressBarEl.setPointerCapture(e.pointerId);
            progressBarEl.addEventListener('pointermove', onPointerMove, { passive: false });
            progressBarEl.addEventListener('pointerup', endDrag);
            progressBarEl.addEventListener('pointercancel', endDrag);
            progressBarEl.addEventListener('pointerleave', onPointerLeave);
          },
          { passive: false },
        );
      };

      const isMobile = window.innerWidth <= HERO_MOBILE_BREAKPOINT;
      const slidesPerView = isMobile ? 1.6 : 1;

      const onEvents = skeletonMode
        ? {
            init: function () {
              updateProgressBar(this);
              updatePaginationDots(this);
            },
          }
        : {
            init: function () {
              updateProgressBar(this);
              updatePaginationDots(this);
              if (progressElement) setupProgressBarDrag(progressElement, this);
              if (typeof onProductImagesInit === 'function') onProductImagesInit();
            },
            touchStart: function () {
              section.classList.add('hero-product-showcase--swiper-dragging');
              updateProgressBar(this);
            },
            touchEnd: function () {
              section.classList.remove('hero-product-showcase--swiper-dragging');
              updateProgressBar(this);
            },
            slideChangeTransitionEnd: function () {
              updateProgressBar(this);
              updatePaginationDots(this);
            },
            touchMove: function () {
              updateProgressBar(this);
            },
            setTranslate: function () {
              updateProgressBar(this);
            },
            resize: function () {
              if (resizeTimeout) clearTimeout(resizeTimeout);
              resizeTimeout = setTimeout(() => {
                updateProgressBar(this);
                updatePaginationDots(this);
                const newIsMobile = window.innerWidth <= HERO_MOBILE_BREAKPOINT;
                this.params.slidesPerView = newIsMobile ? 1.6 : 1;
                this.params.spaceBetween = 0;
                this.update();
              }, 150);
            },
          };

      swiperInstance = createHorizontalSwiperInstance(swiperElement, {
        slidesPerView: slidesPerView,
        spaceBetween: 0,
        centeredSlides: true,
        centeredSlidesBounds: true,
        allowTouchMove: !skeletonMode,
        wheelSensitivity: skeletonMode ? 0 : 2,
        wheelOnAfterTranslate: skeletonMode
          ? null
          : function (swiper, progress) {
              updateProgressBar(swiper, progress);
              const count = swiper.slides.length;
              const activeIndex = count > 1 ? Math.round(progress * (count - 1)) : 0;
              updatePaginationDots(swiper, activeIndex);
            },
        scrollbar: {
          el: section.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        on: onEvents,
      });

      return swiperInstance;
    };
  }

  function createSlideToProgressBarController(progressBarFill) {
    let isWheelScrolling = false;
    const thumbWidthPercent = 20;
    const maxLeft = 100 - thumbWidthPercent;

    function updateProgressBar(swiper, progressOverride) {
      if (!progressBarFill || !swiper) return;
      if (isWheelScrolling && progressOverride === undefined) return;
      const progress =
        progressOverride !== undefined
          ? Math.min(1, Math.max(0, progressOverride))
          : Math.min(1, Math.max(0, swiper.progress));
      progressBarFill.style.left = progress * maxLeft + '%';
    }

    function setupProgressBarDrag(progressBarEl, swiper) {
      if (!progressBarEl || !swiper) return;
      if (progressBarEl._collectionProgressDragSetup) {
        progressBarEl._collectionProgressSwiper = swiper;
        return;
      }
      progressBarEl._collectionProgressDragSetup = true;
      progressBarEl._collectionProgressSwiper = swiper;

      function getProgress(clientX) {
        const rect = progressBarEl.getBoundingClientRect();
        return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      }

      function goToProgress(clientX) {
        const activeSwiper = progressBarEl._collectionProgressSwiper;
        const total = activeSwiper ? activeSwiper.slides.length : 0;
        if (total <= 1) return;
        const slideIndex = Math.min(total - 1, Math.max(0, Math.round(getProgress(clientX) * (total - 1))));
        activeSwiper.slideTo(slideIndex);
        updateProgressBar(activeSwiper);
      }

      let activePointerId = null;

      function onPointerMove(e) {
        if (e.pointerType === 'touch') e.preventDefault();
        goToProgress(e.clientX);
      }

      function endDrag(e) {
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        activePointerId = null;
        progressBarEl.classList.remove('is-dragging');
        try {
          progressBarEl.releasePointerCapture(e.pointerId);
        } catch (err) {}
        progressBarEl.removeEventListener('pointermove', onPointerMove);
        progressBarEl.removeEventListener('pointerup', endDrag);
        progressBarEl.removeEventListener('pointercancel', endDrag);
        progressBarEl.removeEventListener('pointerleave', onPointerLeave);
      }

      function onPointerLeave(e) {
        if (activePointerId !== null && e.pointerId === activePointerId) endDrag(e);
      }

      progressBarEl.addEventListener(
        'pointerdown',
        function (e) {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (e.cancelable) e.preventDefault();
          activePointerId = e.pointerId;
          progressBarEl.classList.add('is-dragging');
          goToProgress(e.clientX);
          progressBarEl.setPointerCapture(e.pointerId);
          progressBarEl.addEventListener('pointermove', onPointerMove, { passive: false });
          progressBarEl.addEventListener('pointerup', endDrag);
          progressBarEl.addEventListener('pointercancel', endDrag);
          progressBarEl.addEventListener('pointerleave', onPointerLeave);
        },
        { passive: false },
      );
    }

    return {
      updateProgressBar,
      setupProgressBarDrag,
      setWheelScrolling: (value) => {
        isWheelScrolling = value;
      },
    };
  }

  if (!window.initYouMayAlsoLikeMainSwiper) {
    window.initYouMayAlsoLikeMainSwiper = function (component, options) {
      const opts = options || {};
      const sliderContainer = component.querySelector('.swiper');
      const paginationEl = component.querySelector('.product-collection-showcase__pagination-wrap .swiper-pagination');
      const scrollbarEl =
        component.querySelector('.swiper-scrollbar') ||
        document.querySelector('.you-may-also-like__container .swiper-scrollbar');
      const preset = window.HORIZONTAL_SWIPER_PRESETS.youMayAlsoLike;

      if (!sliderContainer || typeof Swiper === 'undefined') return null;

      if (
        component.swiper &&
        !component.swiper.destroyed &&
        component.swiper.el === sliderContainer
      ) {
        return component.swiper;
      }

      if (component.swiper) component.swiper.destroy();

      let instance = null;
      instance = createHorizontalSwiperInstance(sliderContainer, {
        slidesPerView: preset.slidesPerView,
        spaceBetween: preset.spaceBetween,
        breakpoints: preset.breakpoints,
        pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
        scrollbar: scrollbarEl ? { el: scrollbarEl, draggable: true } : undefined,
        wheelSensitivity: WHEEL_SENSITIVITY,
        on: {
          init: function () {
            if (typeof window.initProductShowcaseCardImageSwipers === 'function') {
              window.initProductShowcaseCardImageSwipers(component);
            }
            if (typeof opts.onInit === 'function') opts.onInit.call(this, component);
          },
        },
      });

      component.swiper = instance;
      return instance;
    };
  }

  if (!window.initProductCollectionShowcaseMainSwiper) {
    window.initProductCollectionShowcaseMainSwiper = function (options) {
      const {
        swiperElement,
        progressBar,
        progressBarFill,
        paginationEl,
        existingInstance,
        getSlidesPerView,
        onInit,
        skeletonMode = false,
      } = options || {};

      if (!swiperElement || typeof Swiper === 'undefined') return null;
      if (typeof getSlidesPerView !== 'function') return null;

      if (existingInstance) existingInstance.destroy(true, true);

      const progress = createSlideToProgressBarController(progressBarFill);
      let resizeDebounceTimer = null;
      let instance = null;

      const onEvents = skeletonMode
        ? {
            init: function () {
              progress.updateProgressBar(this);
              if (progressBar) progress.setupProgressBarDrag(progressBar, this);
            },
            slideChange: function () {
              progress.updateProgressBar(this);
            },
            progress: function () {
              progress.updateProgressBar(this);
            },
          }
        : {
            init: function () {
              if (typeof onInit === 'function') onInit(this, progress);
              if (progressBar) progress.setupProgressBarDrag(progressBar, this);
            },
            slideChange: function () {
              progress.updateProgressBar(this);
            },
            progress: function () {
              progress.updateProgressBar(this);
            },
            resize: function () {
              if (resizeDebounceTimer) clearTimeout(resizeDebounceTimer);
              resizeDebounceTimer = setTimeout(() => {
                progress.updateProgressBar(this);
              }, 150);
            },
          };

      instance = createHorizontalSwiperInstance(swiperElement, {
        slidesPerView: getSlidesPerView(),
        spaceBetween: 0,
        allowTouchMove: !skeletonMode,
        pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
        wheelSensitivity: skeletonMode ? 0 : WHEEL_SENSITIVITY,
        wheelOnAfterTranslate: skeletonMode
          ? null
          : (swiper, progressValue) => {
              progress.setWheelScrolling(true);
              progress.updateProgressBar(swiper, progressValue);
              progress.setWheelScrolling(false);
            },
        on: onEvents,
      });

      return instance;
    };
  }

  if (!window.initCruiserTechnologySwipers) {
    window.initCruiserTechnologySwipers = function (root) {
      const container = root || document;
      const instances = [];
      const preset = window.HORIZONTAL_SWIPER_PRESETS.cruiserTechnology;

      if (typeof Swiper === 'undefined') {
        setTimeout(() => window.initCruiserTechnologySwipers(container), 100);
        return instances;
      }

      container.querySelectorAll('.technology-slider:not(.swiper-initialized)').forEach((sliderEl) => {
        const section = sliderEl.closest('.technology-section') || sliderEl.closest('section') || container;
        const instance = createHorizontalSwiperInstance(sliderEl, {
          slidesPerView: preset.slidesPerView,
          spaceBetween: preset.spaceBetween,
          loop: preset.loop,
          breakpoints: preset.breakpoints,
          navigation: {
            nextEl: section.querySelector('.technology-next-button'),
            prevEl: section.querySelector('.technology-prev-button'),
          },
          wheelSensitivity: 0,
        });
        instances.push(instance);
      });

      return instances;
    };
  }

  /**
   * Colorway swaps replace section HTML and re-run scripts, but gallery/tech Swipers
   * can miss init (DOMContentLoaded never re-fires) or keep a dead instance. Force a
   * clean rebuild after each successful PDP swap.
   */
  if (!window.__pdpHorizontalSwiperSwapBound) {
    window.__pdpHorizontalSwiperSwapBound = true;

    document.addEventListener('pdp:swapped', () => {
      if (typeof window.initGallerySwipers === 'function') {
        window.initGallerySwipers(document, { force: true });
      }

      if (typeof window.initTechnologySwipers === 'function') {
        window.initTechnologySwipers(document, { force: true });
      }
    });
  }
})();
